/**
 * Saved Reports — typed query snapshots with folder organization.
 *
 * A SavedReport captures the full item list state (filters, sort, groupBy,
 * visible columns, active chart) so it can be restored instantly. Reports
 * are organized into folders and can be linked to a Google Sheets URL for
 * manual or automated export.
 */

import type { FilterGroup, SortSpec } from '../../components/prioritization/item-list/hooks/useItemListState';
import { coerceChartType, type ChartType } from '../../components/prioritization/item-list/components/chartTypes';
import type { QueryNode } from '../../lib/pql/types';
import { astToFilterGroups, filterGroupsToAst } from '../../lib/pql/interop';

// ============================================================================
// Report Folder
// ============================================================================

export interface ReportFolder {
  id: string;
  workspaceId?: string;
  name: string;
  icon?: string;
  /** null = root level */
  parentId: string | null;
  sortOrder: number;
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Saved Report
// ============================================================================

/**
 * Who can see a saved report (migration 1023). `workspace`: everyone in the
 * workspace can open and change it. `private`: only the person who saved it;
 * the screens say "just me". The row rules hold this, not the client.
 */
export type ReportVisibility = 'workspace' | 'private';

/** The sentence a person reads after saving: who can see what they just saved. */
export function audienceSentence(visibility: ReportVisibility): string {
  return visibility === 'private' ? 'Only you can see it.' : 'Everyone in the workspace can see it.';
}

export interface SavedReport {
  id: string;
  workspaceId?: string;
  /** null = root (no folder) */
  folderId: string | null;
  name: string;
  description?: string;
  entityType: 'items' | 'groups' | 'sessions' | string;
  /** Full filter groups (compound AND/OR rules) — builder-representable subset of queryAst. */
  filterGroups: FilterGroup[];
  /**
   * Canonical PQL statement text, when this report was saved from (or
   * carries) a statement the builder can't fully represent — e.g. a Smart
   * Collection's binding rule, which may use negation. Undefined for
   * ordinary builder-created reports; when present, queryAst is the
   * authoritative predicate and filterGroups is a best-effort mirror.
   */
  queryText?: string;
  queryAst?: QueryNode | null;
  /**
   * The saved query this one was copied from, when it was created as a Smart
   * Collection rule or a pool feed (migration 725). Lineage, not a binding:
   * the copy is independent and the two diverge — this is what lets the app
   * say the rule will not follow edits to its origin. Undefined for a query
   * with no saved origin.
   */
  sourceReportId?: string | null;
  sort: SortSpec[];
  groupBy: string | null;
  visibleColumns: string[];
  activeChart: ChartType;
  /** Google Sheets spreadsheet URL, if linked */
  sheetsUrl?: string;
  /** Last time data was pushed to Sheets */
  lastSyncedAt?: Date;
  isPinned?: boolean;
  accessCount?: number;
  lastAccessedAt?: Date;
  createdBy?: string;
  createdByName?: string;
  /** Unset means `workspace`, the default on the row. */
  visibility?: ReportVisibility;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// Factory
// ============================================================================

export function createSavedReport(
  params: Omit<SavedReport, 'id' | 'createdAt' | 'updatedAt'>
): SavedReport {
  const now = new Date();
  return {
    ...params,
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
  };
}

// ============================================================================
// Supabase serialization
// ============================================================================

export function reportToSupabaseFormat(r: SavedReport): Record<string, unknown> {
  return {
    id: r.id,
    workspace_id: r.workspaceId,
    folder_id: r.folderId,
    name: r.name,
    description: r.description,
    entity_type: r.entityType,
    // DEP-034: filters_json is no longer written. The query is stored once, as
    // the PQL AST; a builder-made report's groups become their AST here, and
    // read back as groups below. Rows written before keep their filters_json,
    // which is still read when they carry no AST.
    query_text: r.queryText ?? null,
    query_ast: JSON.stringify(r.queryAst !== undefined ? r.queryAst : filterGroupsToAst(r.filterGroups)),
    source_report_id: r.sourceReportId ?? null,
    sort_json: JSON.stringify(r.sort),
    group_by: r.groupBy,
    visible_columns_json: JSON.stringify(r.visibleColumns),
    active_chart: r.activeChart,
    sheets_url: r.sheetsUrl,
    last_synced_at: r.lastSyncedAt?.toISOString(),
    is_pinned: r.isPinned ?? false,
    access_count: r.accessCount ?? 0,
    last_accessed_at: r.lastAccessedAt?.toISOString(),
    created_by: r.createdBy,
    created_by_name: r.createdByName,
    created_at: r.createdAt.toISOString(),
    updated_at: r.updatedAt.toISOString(),
  };
}

/**
 * The row a new report is inserted with. Who can see a report is written here
 * and through setReportVisibility, never by reportToSupabaseFormat: that is the
 * whole-row save, and a stale tab's copy would otherwise put an old answer back.
 * The default needs no column, so a save still lands on a database that has not
 * had migration 1023 yet.
 */
export function reportInsertRow(r: SavedReport): Record<string, unknown> {
  return {
    ...reportToSupabaseFormat(r),
    ...(r.visibility === 'private' ? { visibility: 'private' } : {}),
  };
}

export function reportFromSupabaseFormat(row: Record<string, unknown>): SavedReport {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string | undefined,
    folderId: (row.folder_id as string) || null,
    name: row.name as string,
    description: row.description as string | undefined,
    entityType: (row.entity_type as string) || 'items',
    filterGroups: readFilterGroups(row),
    queryText: (row.query_text as string) || undefined,
    queryAst: row.query_ast != null ? tryParse<QueryNode | null>(row.query_ast as string, null) : undefined,
    sourceReportId: (row.source_report_id as string) ?? null,
    sort: tryParse(row.sort_json as string, [{ field: 'updatedAt', direction: 'desc' }]),
    groupBy: (row.group_by as string) || null,
    visibleColumns: tryParse(row.visible_columns_json as string, []),
    activeChart: coerceChartType(row.active_chart as string | null | undefined),
    sheetsUrl: row.sheets_url as string | undefined,
    lastSyncedAt: row.last_synced_at ? new Date(row.last_synced_at as string) : undefined,
    isPinned: Boolean(row.is_pinned),
    accessCount: (row.access_count as number) ?? 0,
    lastAccessedAt: row.last_accessed_at ? new Date(row.last_accessed_at as string) : undefined,
    createdBy: row.created_by as string | undefined,
    createdByName: row.created_by_name as string | undefined,
    visibility: row.visibility === 'private' ? 'private' : 'workspace',
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

export function folderToSupabaseFormat(f: ReportFolder): Record<string, unknown> {
  return {
    id: f.id,
    workspace_id: f.workspaceId,
    name: f.name,
    icon: f.icon,
    parent_id: f.parentId,
    sort_order: f.sortOrder,
    created_by: f.createdBy,
    created_at: f.createdAt.toISOString(),
    updated_at: f.updatedAt.toISOString(),
  };
}

export function folderFromSupabaseFormat(row: Record<string, unknown>): ReportFolder {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string | undefined,
    name: row.name as string,
    icon: row.icon as string | undefined,
    parentId: (row.parent_id as string) || null,
    sortOrder: (row.sort_order as number) ?? 0,
    createdBy: row.created_by as string | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

/**
 * The builder's groups for a saved row: from the AST when the row has one
 * and the builder can show it, else from the legacy filters_json (rows saved
 * before DEP-034, or a statement-only query's old best-effort mirror).
 */
function readFilterGroups(row: Record<string, unknown>): SavedReport['filterGroups'] {
  if (row.query_ast != null) {
    const fromAst = astToFilterGroups(tryParse<QueryNode | null>(row.query_ast, null));
    if (fromAst) return fromAst;
  }
  return tryParse(row.filters_json as string, []);
}

function tryParse<T>(json: unknown, fallback: T): T {
  if (json === null || json === undefined || json === '') return fallback;
  // jsonb can arrive already parsed (a row written as a real array/object
  // rather than this app's stringified form) — take it as-is.
  if (typeof json !== 'string') return json as T;
  try { return JSON.parse(json); } catch { return fallback; }
}
